import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { ENVIRONMENT_TYPES, type EnvironmentType } from '@system-atlas/shared';
import { ApiError, api, type ImageCategory, type ImageMeta, type ImageScope } from '../lib/api';
import { Alert, Badge, Button, Card, ConfirmDialog, Label, Modal, Select, Spinner, TextInput } from '../components/ui';
import { useToast } from '../components/toast';
import { cn } from '../lib/utils';

export default function ImagesAdminPage() {
  const { t } = useTranslation();
  const [categories, setCategories] = useState<ImageCategory[]>([]);
  const [images, setImages] = useState<ImageMeta[] | null>(null);
  const [scope, setScope] = useState<ImageScope>('uploads');
  const [error, setError] = useState<string | null>(null);
  const dragImageIndex = useRef<number | null>(null);
  const dragCatIndex = useRef<number | null>(null);
  const [dragImageFrom, setDragImageFrom] = useState<number | null>(null);
  const [dragImageOver, setDragImageOver] = useState<number | null>(null);
  const [dragCatFrom, setDragCatFrom] = useState<number | null>(null);
  const [dragCatOver, setDragCatOver] = useState<number | null>(null);

  const [catName, setCatName] = useState('');
  const [catEnv, setCatEnv] = useState<EnvironmentType>('neutral');
  const [deleteCatTarget, setDeleteCatTarget] = useState<ImageCategory | null>(null);
  const [deleteImageTarget, setDeleteImageTarget] = useState<ImageMeta | null>(null);
  const [editTarget, setEditTarget] = useState<ImageMeta | null>(null);
  const toast = useToast();

  const loadCategories = async () => {
    const { categories: rows } = await api.listImageCategories();
    setCategories(rows);
  };
  const loadImages = async () => {
    const { images: rows } = await api.listImages({ scope });
    setImages(rows);
  };

  const run = async (fn: () => Promise<unknown>) => {
    setError(null);
    try {
      await fn();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('errors.generic'));
    }
  };

  useEffect(() => {
    void run(loadCategories);
  }, []);
  useEffect(() => {
    setImages(null);
    void run(loadImages);
  }, [scope]);

  const categoryName = (id: string | null) => (id ? (categories.find((c) => c.id === id)?.name ?? '') : '');

  const createCategory = async (e: FormEvent) => {
    e.preventDefault();
    if (!catName.trim()) return;
    await run(async () => {
      await api.createImageCategory({ name: catName.trim(), environmentType: catEnv });
      setCatName('');
      await loadCategories();
    });
  };

  const reorder = <T,>(list: T[], from: number, to: number): T[] => {
    const next = [...list];
    const [moved] = next.splice(from, 1);
    if (moved === undefined) return list;
    next.splice(to, 0, moved);
    return next;
  };

  const persistImageOrder = (next: ImageMeta[]) => {
    setImages(next);
    void run(() => api.reorderImages(next.map((img, i) => ({ id: img.id, sortOrder: i }))));
  };

  const persistCategoryOrder = (next: ImageCategory[]) => {
    setCategories(next);
    void run(() => api.reorderImageCategories(next.map((c, i) => ({ id: c.id, sortOrder: i }))));
  };

  const dropImage = (targetIndex: number) => {
    const from = dragImageIndex.current;
    dragImageIndex.current = null;
    setDragImageFrom(null);
    setDragImageOver(null);
    if (from === null || from === targetIndex || !images) return;
    persistImageOrder(reorder(images, from, targetIndex));
  };

  const dropCategory = (targetIndex: number) => {
    const from = dragCatIndex.current;
    dragCatIndex.current = null;
    setDragCatFrom(null);
    setDragCatOver(null);
    if (from === null || from === targetIndex) return;
    persistCategoryOrder(reorder(categories, from, targetIndex));
  };

  // Tastatur-Alternative zum Drag-&-Drop: ein Element um eine Position nach vorne/hinten schieben.
  const moveImage = (from: number, to: number) => {
    if (!images || to < 0 || to >= images.length) return;
    persistImageOrder(reorder(images, from, to));
  };

  const moveCategory = (from: number, to: number) => {
    if (to < 0 || to >= categories.length) return;
    persistCategoryOrder(reorder(categories, from, to));
  };

  return (
    <div className="mx-auto max-w-5xl px-6 py-10">
      <h1 className="text-2xl font-bold tracking-tight">{t('images.adminTitle')}</h1>
      <p className="mt-1 text-sm text-slate-600 dark:text-slate-300">{t('images.adminSubtitle')}</p>

      {error && (
        <div className="mt-4">
          <Alert kind="error">{error}</Alert>
        </div>
      )}

      {/* Kategorien */}
      <Card className="mt-6">
        <h2 className="text-lg font-semibold">{t('images.categories')}</h2>
        <form onSubmit={createCategory} className="mt-3 flex flex-wrap items-end gap-2">
          <div>
            <Label htmlFor="cat-name">{t('images.name')}</Label>
            <TextInput id="cat-name" value={catName} onChange={(e) => setCatName(e.target.value)} />
          </div>
          <div>
            <Label htmlFor="cat-env">{t('editor.environment')}</Label>
            <Select id="cat-env" value={catEnv} onChange={(e) => setCatEnv(e.target.value as EnvironmentType)}>
              {ENVIRONMENT_TYPES.map((env) => (
                <option key={env} value={env}>
                  {t(`environment.${env}`)}
                </option>
              ))}
            </Select>
          </div>
          <Button type="submit">{t('images.addCategory')}</Button>
        </form>
        {categories.length > 0 && (
          <p className="mt-3 text-xs text-slate-500 dark:text-slate-400">{t('images.reorderHint')}</p>
        )}
        <div className="mt-2 flex flex-wrap gap-2">
          {categories.length === 0 && <span className="text-sm text-slate-500">{t('images.noCategories')}</span>}
          {categories.map((c, index) => (
            <span
              key={c.id}
              draggable
              onDragStart={() => {
                dragCatIndex.current = index;
                setDragCatFrom(index);
              }}
              onDragOver={(e) => {
                e.preventDefault();
                setDragCatOver(index);
              }}
              onDragLeave={() => setDragCatOver((prev) => (prev === index ? null : prev))}
              onDragEnd={() => {
                dragCatIndex.current = null;
                setDragCatFrom(null);
                setDragCatOver(null);
              }}
              onDrop={() => dropCategory(index)}
              className={cn(
                'inline-flex cursor-move items-center gap-2 rounded-full bg-slate-100 px-3 py-1 text-sm dark:bg-slate-700',
                dragCatFrom === index && 'opacity-50',
                dragCatOver === index && dragCatFrom !== index && 'ring-2 ring-slate-500 dark:ring-slate-300',
              )}
            >
              {c.name}
              <Badge tone="neutral">{t(`environment.${c.environmentType}`)}</Badge>
              <span className="flex items-center">
                <button
                  type="button"
                  aria-label={t('images.moveUp')}
                  disabled={index === 0}
                  className="px-0.5 text-slate-400 hover:text-slate-700 disabled:opacity-30 dark:hover:text-slate-100"
                  onClick={() => moveCategory(index, index - 1)}
                >
                  ↑
                </button>
                <button
                  type="button"
                  aria-label={t('images.moveDown')}
                  disabled={index === categories.length - 1}
                  className="px-0.5 text-slate-400 hover:text-slate-700 disabled:opacity-30 dark:hover:text-slate-100"
                  onClick={() => moveCategory(index, index + 1)}
                >
                  ↓
                </button>
              </span>
              <button
                type="button"
                aria-label={t('images.delete')}
                className="text-slate-400 hover:text-red-600"
                onClick={() => setDeleteCatTarget(c)}
              >
                ×
              </button>
            </span>
          ))}
        </div>
      </Card>

      {/* Bilder */}
      <Card className="mt-6">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold">{t('images.images')}</h2>
          <Select value={scope} onChange={(e) => setScope(e.target.value as ImageScope)}>
            <option value="uploads">{t('images.scopeUploads')}</option>
            <option value="library">{t('images.scopeLibrary')}</option>
          </Select>
        </div>

        {!images ? (
          <Spinner />
        ) : images.length === 0 ? (
          <p className="py-6 text-center text-sm text-slate-500">{t('images.empty')}</p>
        ) : (
          <>
          <p className="mt-3 text-xs text-slate-500 dark:text-slate-400">{t('images.reorderHint')}</p>
          <div className="mt-2 grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4">
            {images.map((img, index) => (
              <div
                key={img.id}
                draggable
                onDragStart={() => {
                  dragImageIndex.current = index;
                  setDragImageFrom(index);
                }}
                onDragOver={(e) => {
                  e.preventDefault();
                  setDragImageOver(index);
                }}
                onDragLeave={() => setDragImageOver((prev) => (prev === index ? null : prev))}
                onDragEnd={() => {
                  dragImageIndex.current = null;
                  setDragImageFrom(null);
                  setDragImageOver(null);
                }}
                onDrop={() => dropImage(index)}
                className={cn(
                  'cursor-move rounded-md border border-slate-200 p-2 dark:border-slate-700',
                  dragImageFrom === index && 'opacity-50',
                  dragImageOver === index && dragImageFrom !== index && 'ring-2 ring-slate-500 dark:ring-slate-300',
                )}
              >
                <img src={api.imageRawUrl(img.id)} alt={img.name} className="h-24 w-full object-contain" draggable={false} />
                <div className="mt-1 truncate text-xs font-medium" title={img.name}>
                  {img.name}
                </div>
                <div className="flex items-center gap-1 text-xs text-slate-500">
                  <Badge tone={img.isLibrary ? 'green' : 'amber'}>
                    {img.isLibrary ? t('images.inLibrary') : t('images.upload')}
                  </Badge>
                  {img.categoryId && <span className="truncate">{categoryName(img.categoryId)}</span>}
                </div>
                <div className="mt-2 flex flex-wrap items-center gap-1">
                  <Button
                    variant="ghost"
                    className="px-2 py-1 text-xs"
                    aria-label={t('images.moveUp')}
                    disabled={index === 0}
                    onClick={() => moveImage(index, index - 1)}
                  >
                    ↑
                  </Button>
                  <Button
                    variant="ghost"
                    className="px-2 py-1 text-xs"
                    aria-label={t('images.moveDown')}
                    disabled={index === images.length - 1}
                    onClick={() => moveImage(index, index + 1)}
                  >
                    ↓
                  </Button>
                  {!img.isLibrary && (
                    <Button
                      variant="secondary"
                      className="px-2 py-1 text-xs"
                      onClick={() => void run(async () => {
                        await api.promoteImage(img.id);
                        await loadImages();
                      })}
                    >
                      {t('images.promote')}
                    </Button>
                  )}
                  <Button
                    variant="secondary"
                    className="px-2 py-1 text-xs"
                    onClick={() => setEditTarget(img)}
                  >
                    {t('images.edit')}
                  </Button>
                  <Button
                    variant="ghost"
                    className="px-2 py-1 text-xs"
                    onClick={() => setDeleteImageTarget(img)}
                  >
                    {t('images.delete')}
                  </Button>
                </div>
              </div>
            ))}
          </div>
          </>
        )}
      </Card>

      <EditImageModal
        target={editTarget}
        categories={categories}
        onClose={() => setEditTarget(null)}
        onSaved={async () => {
          setEditTarget(null);
          toast.success(t('images.updated'));
          await loadImages();
        }}
        onError={setError}
      />

      <ConfirmDialog
        open={!!deleteCatTarget}
        title={t('images.deleteCategoryTitle')}
        message={t('images.deleteCategoryConfirm', { name: deleteCatTarget?.name ?? '' })}
        confirmLabel={t('common.delete')}
        onCancel={() => setDeleteCatTarget(null)}
        onConfirm={() =>
          void run(async () => {
            const target = deleteCatTarget;
            setDeleteCatTarget(null);
            if (!target) return;
            await api.deleteImageCategory(target.id);
            await loadCategories();
          })
        }
      />

      <ConfirmDialog
        open={!!deleteImageTarget}
        title={t('images.deleteTitle')}
        message={t('images.deleteConfirm', { name: deleteImageTarget?.name ?? '' })}
        confirmLabel={t('common.delete')}
        onCancel={() => setDeleteImageTarget(null)}
        onConfirm={() =>
          void run(async () => {
            const target = deleteImageTarget;
            setDeleteImageTarget(null);
            if (!target) return;
            await api.deleteImage(target.id);
            await loadImages();
          })
        }
      />
    </div>
  );
}

function EditImageModal({
  target,
  categories,
  onClose,
  onSaved,
  onError,
}: {
  target: ImageMeta | null;
  categories: ImageCategory[];
  onClose: () => void;
  onSaved: () => void | Promise<void>;
  onError: (msg: string) => void;
}) {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [env, setEnv] = useState<EnvironmentType>('neutral');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (target) {
      setName(target.name);
      setCategoryId(target.categoryId ?? '');
      setEnv(target.environmentType);
    }
  }, [target]);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!target || !name.trim()) return;
    setBusy(true);
    try {
      await api.updateImage(target.id, {
        name: name.trim(),
        categoryId: categoryId || null,
        environmentType: env,
      });
      await onSaved();
    } catch (err) {
      onError(err instanceof ApiError ? err.message : t('errors.generic'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal open={!!target} title={t('images.editTitle')} onClose={onClose}>
      <form onSubmit={submit} className="flex flex-col gap-4">
        <div>
          <Label htmlFor="ei-name">{t('images.name')}</Label>
          <TextInput id="ei-name" required value={name} onChange={(e) => setName(e.target.value)} />
        </div>
        <div>
          <Label htmlFor="ei-cat">{t('images.categories')}</Label>
          <Select id="ei-cat" className="w-full" value={categoryId} onChange={(e) => setCategoryId(e.target.value)}>
            <option value="">{t('images.noCategory')}</option>
            {categories.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </Select>
        </div>
        <div>
          <Label htmlFor="ei-env">{t('editor.environment')}</Label>
          <Select id="ei-env" className="w-full" value={env} onChange={(e) => setEnv(e.target.value as EnvironmentType)}>
            {ENVIRONMENT_TYPES.map((envOption) => (
              <option key={envOption} value={envOption}>
                {t(`environment.${envOption}`)}
              </option>
            ))}
          </Select>
        </div>
        <div className="flex justify-end gap-2">
          <Button type="button" variant="secondary" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" disabled={busy}>
            {busy ? t('common.pleaseWait') : t('images.save')}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
